// external dependencies
import React, { useState } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { shape, string } from 'prop-types'
import { Link } from '@jelly/ui'

//utils
import {
	buildDataForPushEventStreamData,
	getDistanceFromTop,
	getIp,
	makeImgResizerUrl,
	makeTransactionId,
	pushEventStreamData,
} from '@jelly/utils'

// styled components
const Address = styled.div`
	display: inline-block;
	font-size: 0.75rem;
	line-height: 1.5;
	margin-bottom: 1rem;
`

const LogoWrap = styled.div`
	height: auto;
	width: 5rem;
`

const NAME_STYLES = css`
	font-size: 0.875rem;
	font-weight: 700;
`

const NameLink = styled(Link)`
	${NAME_STYLES}
	color: ${({ theme }) => theme.colors.orange};

	&:hover {
		color: ${({ theme }) => theme.colors.teal};
		cursor: pointer;
	}
`

const Name = styled.div`
	${NAME_STYLES}
`

const Wrapper = styled.div`
	border-bottom-style: solid;
	border-bottom-width: 1px;
	border-color: #ccc;
	color: ${({ theme }) => theme.colors.text};
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	padding: 0.5rem;
`

NearYouService.propTypes = {
	address: string,
	affiliateLink: string,
	city: string,
	guid: string,
	logoUrl: string,
	monetization: shape({ link: string }),
	name: string.isRequired,
	state: string,
}

/**
 * NearYouService
 *
 * Information about a specific company or service intended to be displayed in a searchable table to one side of
 * a rendered Google map. It displays the company name, logo, and address, and should link to the company site.
 */
export function NearYouService({ address, affiliateLink, city, logoUrl, name, state, guid, monetization: { link } }) {
	const [target, setTarget] = useState('')

	const handleClick = (e) => {
		e.preventDefault()
		setTarget(e.currentTarget)

		const bdcTransactionId = makeTransactionId()
		const bodyHeight = document.documentElement.scrollHeight
		const buttonHeight = getDistanceFromTop(e.target)
		const buttonPosition = Math.round((buttonHeight / bodyHeight) * 100)
		const linkToOpen = link ? `${link.replace(/TX-ID-VALUE/g, bdcTransactionId)}` : affiliateLink

		getIp().then((res) => {
			pushEventStreamData(
				buildDataForPushEventStreamData(window.location, target, name, buttonPosition, res.ip, guid)
			)
		})

		// external redirect with noopener protection - https://mathiasbynens.github.io/rel-noopener/
		const newWindow = window.open()

		newWindow.opener = null
		newWindow.location = linkToOpen
	}

	const imgLogo = makeImgResizerUrl({ src: logoUrl, method: 'w', w: 100 })

	return (
		<Wrapper>
			<Address>
				{!!affiliateLink && (
					<NameLink
						href={affiliateLink}
						onClick={handleClick}
						onContextMenu={handleClick}
						target="_blank"
						rel="noopener noreferrer nofollow"
					>
						{name}
					</NameLink>
				)}
				{!affiliateLink && <Name>{name}</Name>}
				{!!address && <div>{address}</div>}
				{!!city && `${city},`}
				{!!state && ` ${state}`}
			</Address>
			{logoUrl && (
				<LogoWrap>
					<img alt={name} src={imgLogo} />
				</LogoWrap>
			)}
		</Wrapper>
	)
}
